import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Award,
  BadgeCheck,
  Code2,
  Headphones,
  Mountain,
  ShieldCheck,
} from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { FinalCTA } from "@/components/home/HomeSections";
import chrisPortrait from "@/assets/chris-heidlebaugh-colorado-web-designer.png";
import {
  SCHEMA_ORG_ID,
  SCHEMA_PERSON_ID,
  SITE_URL,
} from "@/lib/entitySchema";

const CANONICAL = `${SITE_URL}/team`;

type Member = {
  name: string;
  role: string;
  bio: string;
  credentials: string[];
  image?: string;
  personId?: string;
  links?: { label: string; href: string }[];
};

const TEAM: Member[] = [
  {
    name: "Chris Heidlebaugh",
    role: "Owner, Lead Developer & SEO Strategist",
    bio: "Chris founded Colorado Web Impressions in 2006 after building his first commercial website in the late 1990s. Every project the agency takes on is hand-coded and personally led by Chris — no offshore handoffs, no junior account managers, no whitelabel SEO.",
    credentials: [
      "25+ years building and ranking Colorado small-business websites",
      "Google Ads Search Certified (Renewed 2025)",
      "Google Analytics (GA4) Certified (Renewed 2025)",
      "Google Business Profile expert practitioner",
      "Bing Ads Accredited Professional",
      "Member, BBB of Southern Colorado (A+ rating)",
      "Member, Colorado Springs Chamber & EDC",
    ],
    image: chrisPortrait,
    personId: SCHEMA_PERSON_ID,
    links: [
      { label: "Read full bio", href: "/meet-chris" },
      { label: "LinkedIn", href: "https://www.linkedin.com/in/chrisheidlebaugh" },
    ],
  },
];

const SUPPORT_NETWORK = [
  {
    icon: Code2,
    title: "Specialist contractors (US-based, vetted)",
    body: "For specialized work — advanced motion design, custom integrations, large-scale content production — Chris collaborates with a small bench of US-based specialists he's worked with for years. Every contractor is vetted, supervised in-house, and never given lead access without sign-off.",
  },
  {
    icon: ShieldCheck,
    title: "What we never do",
    body: "We do not offshore work, sell to whitelabel reseller agencies, hand off your account to a salesperson, or use AI to generate the substantive copy on your site. The owner does the work — that's the whole point.",
  },
  {
    icon: Headphones,
    title: "Direct owner access",
    body: "Every client gets Chris's direct number and email. No tickets, no tier-1 support layer. When you call, the owner answers.",
  },
];

const TEAM_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "AboutPage",
      "@id": `${CANONICAL}#aboutpage`,
      url: CANONICAL,
      name: "Our Team — Colorado Web Impressions",
      description:
        "Meet the team behind Colorado Web Impressions — owner-operated, in-house, Colorado-based.",
      isPartOf: { "@id": `${SITE_URL}/#website` },
      about: { "@id": SCHEMA_ORG_ID },
      mainEntity: { "@id": SCHEMA_PERSON_ID },
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${CANONICAL}#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Team", item: CANONICAL },
      ],
    },
  ],
};

export const Route = createFileRoute("/team")({
  head: () => ({
    meta: [
      {
        title:
          "Our Team — Owner-Operated Colorado Marketing Agency | Colorado Web Impressions",
      },
      {
        name: "description",
        content:
          "Meet the team behind Colorado Web Impressions. Owner-operated, in-house, Colorado-based — no offshoring, no whitelabel agencies, no junior account managers.",
      },
      { property: "og:title", content: "Our Team — Colorado Web Impressions" },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(TEAM_LD) },
    ],
  }),
  component: TeamPage,
});

function TeamPage() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="The team behind the work"
        title={
          <>
            Owner-led. <span className="text-brand-gold">Colorado-based.</span>
          </>
        }
        description="No offshoring, no whitelabel agencies, no junior account managers. The person who answers your call is the person doing the work."
      />

      {/* Team grid */}
      <section className="bg-background py-20 lg:py-24">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          {TEAM.map((m) => (
            <article
              key={m.name}
              className="grid gap-10 lg:grid-cols-[18rem_minmax(0,1fr)] lg:gap-14"
            >
              <div>
                {m.image && (
                  <div className="aspect-[4/5] overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-brand-gold/20 via-muted to-brand-gold/5 shadow-card">
                    <img
                      src={m.image}
                      alt={`${m.name}, ${m.role} at Colorado Web Impressions`}
                      className="h-full w-full object-cover object-top"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="mt-4 space-y-2 rounded-xl border border-border bg-card p-4 text-sm text-foreground/80 shadow-card">
                  <div className="flex items-center gap-2">
                    <Mountain className="h-4 w-4 text-brand-gold-deep" />
                    Colorado Springs, CO
                  </div>
                  <div className="flex items-center gap-2">
                    <Award className="h-4 w-4 text-brand-gold-deep" />
                    Founder & lead practitioner
                  </div>
                </div>
              </div>

              <div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
                  {m.role}
                </span>
                <h2 className="mt-2 text-3xl font-extrabold leading-tight text-foreground sm:text-4xl">
                  {m.name}
                </h2>
                <p className="mt-4 text-lg leading-relaxed text-foreground/85">
                  {m.bio}
                </p>

                <h3 className="mt-8 flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-brand-gold-deep">
                  <BadgeCheck className="h-4 w-4" />
                  Credentials & memberships
                </h3>
                <ul className="mt-4 grid gap-2.5 sm:grid-cols-2">
                  {m.credentials.map((cred) => (
                    <li
                      key={cred}
                      className="flex items-start gap-2 rounded-lg border border-border bg-card p-3 text-sm text-foreground/85"
                    >
                      <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold-deep" />
                      <span>{cred}</span>
                    </li>
                  ))}
                </ul>

                {m.links && (
                  <div className="mt-6 flex flex-wrap gap-2">
                    {m.links.map((l) =>
                      l.href.startsWith("http") ? (
                        <a
                          key={l.href}
                          href={l.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3.5 py-2 text-sm font-semibold text-foreground transition-smooth hover:border-brand-gold/60 hover:text-brand-gold-deep"
                        >
                          {l.label}
                        </a>
                      ) : (
                        <Link
                          key={l.href}
                          to={l.href}
                          className="inline-flex items-center gap-1.5 rounded-md bg-gradient-gold px-3.5 py-2 text-sm font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
                        >
                          {l.label}
                        </Link>
                      ),
                    )}
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Support network */}
      <section className="bg-section-alt py-20 lg:py-24">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
              Behind the scenes
            </span>
            <h2 className="mt-3 text-3xl font-extrabold leading-tight text-foreground sm:text-4xl">
              How we deliver work without ballooning into a big agency.
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              We deliberately stay small so quality stays high. Here's the
              support model that makes that possible.
            </p>
          </div>

          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            {SUPPORT_NETWORK.map((s) => (
              <div
                key={s.title}
                className="rounded-2xl border border-border bg-card p-6 shadow-card"
              >
                <div className="grid h-11 w-11 place-items-center rounded-lg bg-brand-gold/10 text-brand-gold-deep">
                  <s.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-lg font-extrabold text-foreground">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </SiteLayout>
  );
}
